import React, { useEffect, useState } from "react";
import { Drawer, Divider, Tag, Table, Button, Space } from "antd";
import dayjs from "dayjs";
import styles from "../styles.module.scss";
import ConfirmModal from "../../../../components/modal/ConfirmModal";
import api from "../../../../api";
import { useToast } from "../../../../components/toast";

export default function DetailDrawer({ open, onClose, record, trigger }) {
  const [showModal, setShowModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closed, setClosed] = useState(false);
  const [closing, setClosing] = useState(false);
  const toast = useToast();

  useEffect(() => {
    setClosed(!!record?.is_closed);
  }, [record]);

  if (!record) {
    return null;
  }

  const starts = record.starts_at ? dayjs(record.starts_at) : null;
  const ends = record.ends_at ? dayjs(record.ends_at) : null;
  const isFull =
    typeof record.capacity === "number" &&
    typeof record.booked_count === "number" &&
    record.booked_count >= record.capacity;

  const users = Array.isArray(record.users) ? record.users : [];

  const userColumns = [
    {
      title: "И-мэйл",
      dataIndex: "email",
      key: "email",
      render: (v) => v || "-",
    },
    {
      title: "Нэр",
      dataIndex: "username",
      key: "username",
      render: (v) => v || "-",
    },
    {
      title: "Утас",
      dataIndex: "phone",
      key: "phone",
      render: (v) => v || "-",
    },
  ];

  const closeHandler = async () => {
    setClosing(true);
    const next = !closed;
    const res = await api.set_schedule_closed(record.id, next);
    setClosing(false);
    if (res.status === 200) {
      setClosed(next);
      toast(next ? "Бүртгэл хаагдлаа" : "Бүртгэл нээгдлээ", "success");
      trigger?.();
    } else {
      toast(res?.data?.message || "Алдаа гарлаа", "error");
    }
  };

  const deleteHandler = async () => {
    const res = await api.delete_schedule(record.id);
    if (res.status === 200) {
      toast("Амжилттай устгалаа", "success");
      onClose?.(true);
      trigger?.();
    } else {
      toast(res?.data?.message || "Устгах үед алдаа гарлаа", "error");
    }
  };

  return (
    <>
      <Drawer
        title="Дэлгэрэнгүй"
        open={open}
        onClose={() => onClose?.(false)}
        width={720}
        styles={{ body: { paddingBottom: 16 } }}
        extra={
          <Space>
            <Button
              variant={closed ? "solid" : "outlined"}
              color={closed ? "primary" : "default"}
              loading={closing}
              onClick={() => setShowCloseModal(true)}
            >
              {closed ? "Бүртгэл нээх" : "Бүртгэл хаах"}
            </Button>
            <Button
              variant="dashed"
              color="danger"
              style={{ paddingLeft: 30, paddingRight: 30 }}
              onClick={() => setShowModal(true)}
            >
              Устгах
            </Button>
          </Space>
        }
      >
        {/* Ерөнхий мэдээлэл */}
        <Divider
          orientation="left"
          style={{
            borderColor: "#ddd",
            fontSize: 14,
            fontWeight: 600,
            marginBottom: 15,
          }}
        >
          Хуваарийн мэдээлэл
        </Divider>

        <div className={styles.detailRow}>
          <div className={styles.detailLabel}>Огноо:</div>
          <div className={styles.detailValue}>
            {starts ? starts.format("YYYY-MM-DD") : "-"}
          </div>
        </div>

        <div className={styles.detailRow}>
          <div className={styles.detailLabel}>Цаг:</div>
          <div className={styles.detailValue}>
            {starts && ends
              ? `${starts.format("HH:mm")} - ${ends.format("HH:mm")}`
              : "-"}
          </div>
        </div>

        <div className={styles.detailRow}>
          <div className={styles.detailLabel}>Дүүргэлт:</div>
          {/* <div className={styles.detailValue}>
            <Tag color={isFull ? "red" : "green"}>
              {record.booked_count ?? 0} / {record.capacity ?? "-"}
            </Tag>
          </div> */}
          <div className={styles.detailValue}>
            <Tag color={isFull ? "red" : "green"}>
              {record.booked_count ?? 0} / {record.capacity ?? "-"}
            </Tag>
            {closed && <Tag color="default">Хаагдсан</Tag>}
          </div>
        </div>

        {/* Book хийсэн хэрэглэгчид */}
        <Divider
          orientation="left"
          style={{
            borderColor: "#ddd",
            fontSize: 14,
            fontWeight: 600,
            marginTop: 30,
            marginBottom: 15,
          }}
        >
          Захиалсан хэрэглэгчид
        </Divider>
        <Table
          rowKey="id"
          dataSource={users}
          columns={userColumns}
          pagination={false}
          size="small"
          bordered
        />
      </Drawer>

      <ConfirmModal
        showModal={showModal}
        setShowModal={setShowModal}
        submitHandler={deleteHandler}
        variant="warning"
        title="Итгэлтэй байна уу?"
        description={
          <>
            '
            <span style={{ fontWeight: 600, color: "#000" }}>
              Хуваарь устгах
            </span>
            ' үйлдэл нь буцаах боломжгүйг анхаарна уу!
          </>
        }
      />

      <ConfirmModal
        showModal={showCloseModal}
        setShowModal={setShowCloseModal}
        submitHandler={closeHandler}
        variant="warning"
        title="Итгэлтэй байна уу?"
        description={
          closed ? (
            <>
              Энэ хуваарийн бүртгэлийг дахин{" "}
              <span style={{ fontWeight: 600, color: "#000" }}>'нээх'</span> гэж
              байна. Хэрэглэгчид сул суудал байвал захиалга хийх боломжтой
              болно.
            </>
          ) : (
            <>
              Энэ хуваарийн бүртгэлийг{" "}
              <span style={{ fontWeight: 600, color: "#000" }}>'хаах'</span> гэж
              байна. Шинэ захиалга авахгүй, харин одоо захиалсан{" "}
              {record.booked_count ?? 0} хэрэглэгч хэвээр үлдэнэ.
            </>
          )
        }
      />
    </>
  );
}
